import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { AuthMenu } from "@/components/AuthMenu";
import { useCurrentUser } from "@/hooks/useAuth";
import { useFeatures } from "@/hooks/useFeatures";
import {
  useStartTelegramLink,
  useTelegramLinkStatus,
  useTelegramStatus,
  useUnlinkTelegram,
} from "@/hooks/useTelegram";
import {
  useReplaceBlacklist,
  useUpdateUserSettings,
  useUserBlacklist,
} from "@/hooks/useUserData";

export function SettingsPage() {
  const { data: user, isLoading: authLoading } = useCurrentUser();
  const { data: blacklist = [] } = useUserBlacklist();
  const { mutate: replaceBlacklist, isPending } = useReplaceBlacklist();

  const [draft, setDraft] = useState("");

  // Keep the textarea in sync when the server-side list changes (e.g. after save).
  useEffect(() => {
    setDraft(blacklist.join("\n"));
  }, [blacklist]);

  if (authLoading) return null;

  if (!user) {
    return (
      <CenterLayout>
        <p className="text-zinc-400 text-sm">Sign in to manage your settings.</p>
        <div className="flex gap-2 justify-center pt-2">
          <Link to="/login" className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded text-sm">Log in</Link>
          <Link to="/register" className="bg-surface-2 hover:bg-surface-3 text-zinc-300 px-4 py-2 rounded text-sm">Register</Link>
        </div>
      </CenterLayout>
    );
  }

  function handleSave() {
    const tags = draft
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    replaceBlacklist(tags);
  }

  return (
    <>
      <Helmet>
        <title>Settings — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Link to="/" className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center">← Back</Link>
            <h1 className="text-sm font-semibold">Settings</h1>
            <div className="ml-auto">
              <AuthMenu />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold">Personal blacklist</h2>
              <p className="text-xs text-zinc-500 mt-1">
                Tags listed here are excluded from every search you do while signed in,
                on top of the hard safety filter. Stored on the server, synced across devices.
              </p>
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.max(6, Math.min(20, draft.split("\n").length + 1))}
              placeholder="One tag per line, or comma/space-separated"
              className="w-full bg-surface-2 border border-surface-3 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={isPending}
                className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white text-sm font-semibold px-4 py-1.5 rounded"
              >
                {isPending ? "Saving…" : "Save blacklist"}
              </button>
              <span className="text-xs text-zinc-600">
                {blacklist.length} tag{blacklist.length === 1 ? "" : "s"} currently saved
              </span>
            </div>
          </section>

          <ImageSearchSection />

          <TelegramLinkSection />

          <p className="text-xs text-zinc-600 mt-8">
            Looking for your saved searches?{" "}
            <Link to="/saved-searches" className="text-accent-light hover:underline">
              Manage them here →
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

/** Per-user image-search opt-in. Hidden entirely when the operator hasn't
 *  enabled image indexing on this deployment — the toggle wouldn't do
 *  anything useful. */
function ImageSearchSection() {
  const { data: user } = useCurrentUser();
  const { data: features } = useFeatures();
  const { mutate: updateSettings, isPending } = useUpdateUserSettings();

  if (!features?.images_indexed || !user) return null;

  function onToggle(next: boolean) {
    updateSettings({ include_images: next });
  }

  return (
    <section className="space-y-3 mt-8 pt-6 border-t border-surface-2">
      <div>
        <h2 className="text-sm font-semibold">Image search</h2>
        <p className="text-xs text-zinc-500 mt-1">
          By default only animations (webm / gif / apng) are shown. Enable
          this to also browse static images (jpg / png / webp) from e621.
          Your favorites and downloads are unaffected.
        </p>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={user.include_images}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={isPending}
          className="accent-accent"
        />
        <span className="text-sm">Include static images in search results</span>
      </label>
    </section>
  );
}

/** Per-user Telegram linking. Renders nothing when the operator hasn't
 *  configured the bot at all — no point teasing a feature that won't work.
 *  When configured: shows "Link Telegram" button (opens t.me/<bot>?start=
 *  <token> in a new tab) or "Linked" + unlink button depending on state. */
function TelegramLinkSection() {
  const { data: tgStatus } = useTelegramStatus();
  const { data: link, refetch: refetchLink } = useTelegramLinkStatus(
    !!tgStatus?.configured,
  );
  const { mutate: startLink, isPending: starting } = useStartTelegramLink();
  const { mutate: doUnlink, isPending: unlinking } = useUnlinkTelegram();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!tgStatus?.configured) return null;

  function onLink() {
    setErrorMsg(null);
    startLink(undefined, {
      onSuccess: (r) => {
        // Open the t.me deep link — Telegram (or the web app) will prompt
        // the user to tap Start. Status auto-refreshes on focus when they
        // come back to this tab.
        window.open(r.start_url, "_blank", "noopener");
      },
      onError: (e) => setErrorMsg(e.message),
    });
  }

  function onUnlink() {
    setErrorMsg(null);
    if (
      !window.confirm(
        "Unlink your Telegram account? You'll stop getting DM sends until you re-link.",
      )
    ) {
      return;
    }
    doUnlink(undefined, {
      onError: (e) => setErrorMsg(e.message),
    });
  }

  return (
    <section className="space-y-3 mt-8 pt-6 border-t border-surface-2">
      <div>
        <h2 className="text-sm font-semibold">Telegram</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Link your Telegram account so the bot can send converted GIFs and
          MP4s directly to your DMs — in addition to the public channel.
          Once linked, every Send-to-Telegram surface gets a second blue
          "DM" button.
        </p>
      </div>
      {errorMsg && (
        <div className="bg-red-900/30 border border-red-800 text-red-200 text-xs rounded p-2">
          {errorMsg}
        </div>
      )}
      {link?.linked ? (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-green-400">✓ Telegram linked</span>
          <button
            onClick={onUnlink}
            disabled={unlinking}
            className="text-xs px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-300 disabled:opacity-50"
          >
            {unlinking ? "Unlinking…" : "Unlink"}
          </button>
          <button
            onClick={() => refetchLink()}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 px-2"
          >
            ↻ refresh
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={onLink}
            disabled={starting}
            className="text-sm bg-blue-700 hover:bg-blue-600 text-white font-semibold px-4 py-2 min-h-[40px] rounded disabled:opacity-50"
          >
            {starting ? "Starting…" : "✈ Link Telegram"}
          </button>
          <span className="text-[11px] text-zinc-500 leading-relaxed">
            Opens our bot in Telegram. Tap <strong>Start</strong> there —
            this page will show "linked" within a couple of seconds.
          </span>
        </div>
      )}
    </section>
  );
}

function CenterLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Helmet>
        <title>Settings — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-surface-0 flex items-center justify-center px-6">
        <div className="max-w-sm w-full bg-surface-1 border border-surface-3 rounded-lg p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold">Settings</h1>
          {children}
        </div>
      </div>
    </>
  );
}
